import React from 'react';
import { motion } from 'framer-motion';
import { FaEdit, FaTrash, FaEye, FaEyeSlash, FaClock, FaUser, FaTag } from 'react-icons/fa';

const BlogPostCard = ({
    post,
    onEdit,
    onDelete,
    onToggleStatus,
    onView,
    viewMode = 'grid'
}) => {
    const handleEdit = (e) => {
        e.stopPropagation();
        onEdit && onEdit(post);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete && onDelete(post);
    };

    const handleToggleStatus = (e) => {
        e.stopPropagation();
        onToggleStatus && onToggleStatus(post);
    };

    const handleView = (e) => {
        e.stopPropagation();
        onView && onView(post);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'published':
                return 'bg-green-100 text-green-800';
            case 'draft':
                return 'bg-yellow-100 text-yellow-800';
            case 'archived':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (viewMode === 'list') {
        return (
            <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
            >
                <div className="flex">
                    {/* Image on the left */}
                    <div className="w-48 flex-shrink-0">
                        {post.featured_image ? (
                            <img
                                src={post.featured_image}
                                alt={post.title}
                                className="w-full h-full object-contain"
                            />
                        ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                <span className="text-gray-400 text-sm">No Image</span>
                            </div>
                        )}
                    </div>

                    {/* Content on the right */}
                    <div className="flex-1 p-4 flex flex-col">
                        {/* Header with title, excerpt and buttons */}
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex-1 min-w-0 mr-4">
                                <h3 className="text-lg font-semibold text-gray-900 line-clamp-1 mb-2">
                                    {post.title}
                                </h3>
                                <p className="text-sm text-gray-600 line-clamp-2">
                                    {post.excerpt}
                                </p>
                            </div>

                            <div className="flex items-center space-x-1 flex-shrink-0">
                                <button
                                    onClick={handleView}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                    title="View"
                                >
                                    <FaEye size={12} />
                                </button>
                                <button
                                    onClick={handleToggleStatus}
                                    className={`p-1.5 rounded-full transition-colors ${post.status === 'published'
                                        ? 'text-green-600 hover:bg-green-50'
                                        : 'text-gray-400 hover:bg-gray-50'
                                        }`}
                                    title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                                >
                                    {post.status === 'published' ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                                </button>
                                <button
                                    onClick={handleEdit}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                                    title="Edit"
                                >
                                    <FaEdit size={12} />
                                </button>
                                <button
                                    onClick={handleDelete}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                                    title="Delete"
                                >
                                    <FaTrash size={12} />
                                </button>
                            </div>
                        </div>

                        {/* Category and Tags */}
                        <div className="flex items-center space-x-4 mb-3">
                            {post.category && (
                                <div className="flex items-center space-x-1">
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: post.category.color }}
                                    ></div>
                                    <span className="text-sm text-gray-600">
                                        {post.category.name}
                                    </span>
                                </div>
                            )}

                            {post.tags && post.tags.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <FaTag size={12} className="text-gray-400" />
                                    <span className="text-sm text-gray-600">
                                        {post.tags.length} tag{post.tags.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Stats and Footer in one row */}
                        <div className="flex items-center justify-between text-sm text-gray-500 mt-auto">
                            <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-1">
                                    <FaClock size={12} />
                                    <span>{post.estimated_reading_time} min</span>
                                </div>
                                <div className="flex items-center space-x-1">
                                    <FaEye size={12} />
                                    <span>{post.views_count} views</span>
                                </div>
                                {post.author && (
                                    <div className="flex items-center space-x-1">
                                        <FaUser size={12} />
                                        <span className="truncate max-w-24">{post.author.name || post.author.email}</span>
                                    </div>
                                )}
                                <span>
                                    {post.published_date
                                        ? new Date(post.published_date).toLocaleDateString()
                                        : new Date(post.created_at).toLocaleDateString()
                                    }
                                </span>
                            </div>

                            <div className="flex items-center space-x-2">
                                {post.is_featured && (
                                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                        Featured
                                    </span>
                                )}
                                {post.is_priority && (
                                    <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                        Priority
                                    </span>
                                )}
                                <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(post.status)}`}>
                                    {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    // Grid view (default)
    return (
        <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
        >
            {/* Featured Image */}
            {post.featured_image && (
                <div className="h-48 w-full overflow-hidden">
                    <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                </div>
            )}

            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 mb-2">
                            {post.title}
                        </h3>
                        <p className="text-sm text-gray-600 line-clamp-2">
                            {post.excerpt}
                        </p>
                    </div>

                    <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                        <button
                            onClick={handleView}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="View"
                        >
                            <FaEye size={12} />
                        </button>
                        <button
                            onClick={handleToggleStatus}
                            className={`p-1.5 rounded-full transition-colors ${post.status === 'published'
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-gray-400 hover:bg-gray-50'
                                }`}
                            title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                        >
                            {post.status === 'published' ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                        </button>
                        <button
                            onClick={handleEdit}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                            title="Edit"
                        >
                            <FaEdit size={12} />
                        </button>
                        <button
                            onClick={handleDelete}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                            title="Delete"
                        >
                            <FaTrash size={12} />
                        </button>
                    </div>
                </div>

                {/* Category and Tags */}
                <div className="flex items-center space-x-4 mb-3">
                    {post.category && (
                        <div className="flex items-center space-x-1">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: post.category.color }}
                            ></div>
                            <span className="text-sm text-gray-600">
                                {post.category.name}
                            </span>
                        </div>
                    )}

                    {post.tags && post.tags.length > 0 && (
                        <div className="flex items-center gap-2">
                            <FaTag size={12} className="text-gray-400" />
                            <span className="text-sm text-gray-600">
                                {post.tags.length} tag{post.tags.length !== 1 ? 's' : ''}
                            </span>
                        </div>
                    )}
                </div>

                {/* Stats */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-1">
                            <FaClock size={12} />
                            <span>{post.estimated_reading_time} min read</span>
                        </div>
                        <div className="flex items-center space-x-1">
                            <FaEye size={12} />
                            <span>{post.views_count} views</span>
                        </div>
                        {post.author && (
                            <div className="flex items-center space-x-1">
                                <FaUser size={12} />
                                <span>{post.author.name || post.author.email}</span>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center space-x-2">
                        {post.is_featured && (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
                                Featured
                            </span>
                        )}
                        {post.is_priority && (
                            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                                Priority
                            </span>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-200">
                    <span>
                        {post.published_date
                            ? `Published: ${new Date(post.published_date).toLocaleDateString()}`
                            : `Created: ${new Date(post.created_at).toLocaleDateString()}`
                        }
                    </span>
                    <span className={`px-2 py-1 rounded-full ${getStatusColor(post.status)}`}>
                        {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                    </span>
                </div>
            </div>
        </motion.div>
    );
};

export default BlogPostCard;
